import React from 'react';
import { MessageCircle, Mail, MapPin, ExternalLink } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export const Footer = () => {
  const location = useLocation();
  const isFundMaster = location.pathname === '/fundmaster';
  const brandName = isFundMaster ? 'FundMaster' : 'IndexMaster';
  const getFormUrl = () => {
    return isFundMaster ? 'https://forms.gle/5Ka2gvuC9peDhBWv7' : 'https://forms.gle/ZfyxmjxEBEwmtEhs8';
  };
  
  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            {/* Company Info */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-white border border-slate-600 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_index-wealth-1/artifacts/7uycqeio_designlogo.png" 
                    alt="IndexMaster Logo" 
                    className="w-6 h-6 object-contain"
                    style={{
                      filter: 'brightness(0) saturate(100%) invert(29%) sepia(100%) saturate(1000%) hue-rotate(200deg) brightness(80%) contrast(150%) drop-shadow(0 0 1px rgb(37, 99, 235))'
                    }}
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">{brandName}</h3>
                  <p className="text-slate-300 text-sm">Investment Approach</p>
                </div>
              </div>
              
              <p className="text-slate-300 leading-relaxed mb-6">
                {isFundMaster 
                  ? "An open-ended Mutual Funds investing PMS designed to deliver consistent superior risk-adjusted returns through innovative Core & Satellite approach. Managed by Composite Investments Private Limited, a SEBI registered Portfolio Manager focused on index beating performance."
                  : "An innovative Active Index investing Portfolio Management Service that combines the best of both active and passive investing strategies. Managed by Composite Investments Private Limited, a SEBI registered Portfolio Manager focused on delivering superior risk-adjusted returns."
                }
              </p>
              
              <div className="bg-slate-800 rounded-lg p-4 mb-6">
                <p className="text-slate-300 text-sm font-semibold mb-1">SEBI Registration</p>
                <p className="text-blue-400 font-mono text-sm">PMS Registration No. INP000006396</p>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center space-x-3">
                  <MessageCircle className="w-4 h-4 text-green-400" />
                  <a href="https://wa.me/918088920016" target="_blank" rel="noopener noreferrer" 
                     className="text-slate-300 hover:text-green-400 transition-colors">
                    +91 80889 20016 (WhatsApp)
                  </a>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="w-4 h-4 text-green-400" />
                  <span className="text-slate-300">anuraag@compositeinvestments.com</span>
                </div>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-4 h-4 text-blue-400 mt-1" />
                  <span className="text-slate-300 leading-relaxed">
                    K903, SNN Raj Lake View, BTM Layout 2nd Stage, Bangalore 560076, India
                  </span>
                </div>
              </div>
            </div>

            {/* Investment Approach */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Investment Approach</h4>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Active Index Strategy</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Rule-Based Investing</li>
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Tactical Asset Allocation</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">ETF Diversification</li>
                <li className="hover:text-blue-400 transition-colors cursor-pointer">Risk Management</li>
                <li className="hover:text-green-400 transition-colors cursor-pointer">Long-Term Growth</li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-3 text-sm text-slate-300">
                <li>
                  <a href="#approach" className="hover:text-blue-400 transition-colors flex items-center">
                    Our Approach
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <a href="#manager" className="hover:text-green-400 transition-colors flex items-center">
                    Fund Manager
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <a href="#benefits" className="hover:text-blue-400 transition-colors flex items-center">
                    Benefits
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-green-400 transition-colors flex items-center">
                    Contact Us
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://forms.gle/G7CPParsvDGe49Ck9" target="_blank" rel="noopener noreferrer" className="hover:text-orange-400 transition-colors flex items-center">
                    Partner with Us
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li>
                  <a href="https://www.orbisonline.in/portal/account/login.aspx" target="_blank" rel="noopener noreferrer" className="hover:text-purple-400 transition-colors flex items-center">
                    Client Login
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </a>
                </li>
                <li className="pt-3">
                  <div 
                    onClick={() => window.open(getFormUrl(), '_blank')}
                    className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-2 rounded-lg text-center hover:from-blue-600 hover:to-green-600 transition-all cursor-pointer"
                  >
                    Open Account Now
                  </div>
                </li>
                <li className="pt-2">
                  <a 
                    href={isFundMaster ? "/FundMaster_Investment_Brochure.pdf" : "/IndexMaster_Investment_Brochure.pdf"}
                    download={isFundMaster ? "FundMaster_Investment_Brochure.pdf" : "IndexMaster_Investment_Brochure.pdf"}
                    className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-4 py-2 rounded-lg text-center hover:from-green-600 hover:to-blue-600 transition-all cursor-pointer block"
                  >
                    Investment Brochure
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-700 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-slate-400">
              <p>
                © 2025 Composite Investments Private Limited. All rights reserved.
              </p>
              <p className="mt-1">
                IndexMaster is a registered investment approach offered by Composite Investments Private Limited.
              </p>
            </div>
            
            <div className="flex items-center space-x-6 text-sm text-slate-400">
              <Link to="/privacy-policy" className="hover:text-blue-400 transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-green-400 transition-colors">Terms of Service</Link>
              <Link to={isFundMaster ? "/" : "/fundmaster"} className="hover:text-purple-400 transition-colors">
                {isFundMaster ? "IndexMaster" : "FundMaster"}
              </Link>
            </div>
          </div>
          
          {/* Regulatory Disclaimer */}
          <div className="mt-6 p-4 bg-slate-800 rounded-lg">
            <p className="text-xs text-slate-400 leading-relaxed">
              <strong className="text-slate-300">Investment Disclaimer:</strong> 
              Investments in securities market are subject to market risks. Please read all the related documents 
              carefully before investing. Past performance is not indicative of future results. 
              Composite Investments Private Limited is a SEBI registered Portfolio Manager (PMS Registration No. INP000006396). 
              Please consult your financial advisor before making any investment decisions.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};